/**
 * Credit Transactions service for handling credit history API calls
 */

import API_CONFIG from "../apiConfig";

// Types for Order History
export interface OrderTransaction {
    id: string;
    user_id: string;
    amount: number;
    currency: string;
    plan: string;
    plan_display_name: string;
    credits_received: number; // Float value
    status: string;
    created_at: string;
    quantity: number;
}

export interface OrderHistoryResponse {
    success: boolean;
    statusCode: number;
    user_id: string;
    current_balance: number; // Float value
    order_history: OrderTransaction[];
    total_orders: number;
    total_credits_purchased: number; // Float value
}

export interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

// Types for Generation History
export interface GenerationTransaction {
    id: string;
    user_id: string;
    generation_id: string;
    generation_type: string;
    display_name: string;
    prompt: string;
    credits_used: number; // Float value
    timestamp: string;
    details: {
        numberOfImages: number;
        numberOfVideos: number;
        styleName: string;
        imageUrls: string[];
        videoUrls: string[];
        inputImageUrls: string[];
    };
}

export interface UsageByType {
    [key: string]: {
        count: number;
        total_credits: number; // Float value
    };
}

export interface GenerationHistoryResponse {
    success: boolean;
    statusCode: number;
    user_id: string;
    current_balance: number; // Float value
    generation_history: GenerationTransaction[];
    total_generations: number;
    total_credits_used: number; // Float value
    usage_by_type: UsageByType;
}

/**
 * Get credit order history for a user
 * @param userId The user id to get order history for
 * @returns Promise with the order history response
 */
export const getCreditOrderHistory = async (userId: string): Promise<OrderHistoryResponse> => {
    try {
        const response = await fetch(`${API_CONFIG.CREDIT_ORDER_HISTORY_URL}/${userId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Error fetching credit order history: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        throw error;
    }
};

/**
 * Get credit generation/usage history for a user
 * @param userId The user id to get generation history for
 * @returns Promise with the generation history response
 */
export const getCreditGenerationHistory = async (userId: string): Promise<GenerationHistoryResponse> => {
    try {
        const response = await fetch(`${API_CONFIG.CREDIT_GENERATION_HISTORY_URL}/${userId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Error fetching credit generation history: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        throw error;
    }
}; 